<template>
  <div 
    class="service-card"
    :class="{ 
      'selected': isSelected, 
      'disabled': isDisabled 
    }"
    :style="{ '--card-color': color }"
    @click="handleClick"
  >
    <div class="card-title-container">
      <h4 class="card-title">{{ label }}</h4>
    </div>
    <div class="card-description-container">
      <p class="card-description">{{ description }}</p>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

export default defineComponent({
  name: 'ServiceCard',
  props: {
    icon: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: true
    },
    description: {
      type: String,
      required: true
    },
    color: {
      type: String,
      required: true
    },
    isSelected: {
      type: Boolean,
      default: false
    },
    isDisabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['select'],
  setup(props, { emit }) {
    const handleClick = () => {
      if (!props.isDisabled) {
        emit('select')
      }
    }

    return {
      handleClick
    }
  }
})
</script>

<style scoped>
.service-card {
  position: relative;
  background: var(--card-color, #7ee9b5);
  border: 2px solid var(--vt-c-accent);
  border-radius: var(--base-layout-card-border-radius);
  padding: clamp(0.75rem, 2vw, 1.25rem);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: var(--card-gap);
  min-height: clamp(5rem, 12vw, 8rem);
  width: 100%;
  box-shadow: var(--shadow-panel);
}

.service-card:hover:not(.disabled) {
  transform: translateY(-4px);
  box-shadow:
    0 10px 26px rgba(80, 229, 255, 0.42),
    0 0 28px rgba(80, 229, 255, 0.38);
  border-color: #50e5ff;
}

.service-card.selected {
  border-color: var(--vt-c-accent);
  border-width: 2px;
  background: #50e5ff;
  box-shadow: 0 12px 30px rgba(80, 229, 255, 0.32);
  transform: translateY(-4px);
}

.service-card.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  box-shadow: none;
  transform: none;
  border-color: var(--vt-c-border);
}

.card-title-container {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  width: 100%;
}

.card-description-container {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
}

.card-icon {
  display: none;
}

.card-title {
  font-size: var(--base-layout-card-title-size);
  font-weight: 700;
  color: #212543;
  margin: 0;
  line-height: var(--line-heading);
}

.card-description {
  font-size: var(--base-layout-card-description-size);
  color: #212543;
  line-height: var(--line-body);
  margin: 0;
  word-break: break-word;
}

@media (max-width: 768px) {
  .service-card {
    border-radius: 999px;
    min-height: 3.05rem;
    height: 100%;
    padding: 0.49rem 0.67rem;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    text-align: left;
    gap: 0.42rem;
  }

  .card-title-container {
    align-items: center;
    justify-content: flex-start;
    width: auto;
    flex: 0 0 auto;
  }

  .card-description-container {
    align-items: center;
    justify-content: flex-start;
    width: auto;
    flex: 1 1 auto;
    min-width: 0;
  }

  .card-title,
  .card-description {
    line-height: 1.15;
  }
}

/* Accessibility */
@media (prefers-reduced-motion: reduce) {
  .service-card {
    transition: none;
  }
  
  .service-card:hover:not(.disabled) {
    transform: none;
  }
}
</style>
